import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import jsonLogic from 'json-logic-js';
import api from '../axiosInstance';
import { LanguageContext } from '../LanguageContext';
import { registry } from '../renderer/renderers';

const copy = {
  en: {
    financialChanged: 'Your financial information changed while this form was open. Reload the latest figures and review them before signing.',
    reloadFinancial: 'Reload latest figures',
    title: 'Document',
    loading: 'Loading document…',
    loadError: 'We could not load this document. Please try again.',
    schemaMissing: 'This document is not available right now.',
    submit: 'Submit',
    savePdf: 'Save as PDF',
    submitted: 'Submitted',
    submittedMsg: 'Your document has been submitted.',
    back: 'Back to documents',
    required: 'This field is required',
    resumedLocked: 'Your previous submission attempt was saved. These answers are read-only so you can safely submit the same document again.',
    resumedLockedLegend: 'Saved answers (read-only)',
    lineageRepair: 'PATH staff or support must correct this document before it can be submitted. Please contact your PATH staff member or PATH support.'
  },
  fr: {
    financialChanged: 'Vos renseignements financiers ont changé pendant que ce formulaire était ouvert. Rechargez les données actuelles et vérifiez-les avant de signer.',
    reloadFinancial: 'Recharger les données actuelles',
    title: 'Document',
    loading: 'Chargement du document…',
    loadError: 'Impossible de charger ce document. Veuillez réessayer.',
    schemaMissing: 'Ce document n’est pas disponible pour le moment.',
    submit: 'Soumettre',
    savePdf: 'Enregistrer en PDF',
    submitted: 'Soumis',
    submittedMsg: 'Votre document a été soumis.',
    back: 'Retour aux documents',
    required: 'Ce champ est requis',
    resumedLocked: 'Votre tentative d’envoi précédente a été enregistrée. Ces réponses sont en lecture seule afin que vous puissiez soumettre de nouveau exactement le même document.',
    resumedLockedLegend: 'Réponses enregistrées (lecture seule)',
    lineageRepair: 'Le personnel ou le soutien de PATH doit corriger ce document avant qu’il puisse être soumis. Veuillez communiquer avec votre membre du personnel PATH ou le soutien de PATH.'
  }
};

const RETRYABLE_SIGNING_ERRORS = new Set([
  'signing_in_progress',
  'signing_retry_required'
]);
const SIGNING_REPAIR_ERRORS = new Set([
  'signing_completion_repair_required',
  'signing_lineage_repair_required',
]);
const SIGNING_RETRY_DELAYS_MS = [250, 750];

const wait = (delayMs) => new Promise(resolve => setTimeout(resolve, delayMs));

const cloneJsonValue = (value) => JSON.parse(JSON.stringify(value));

const deepFreezeJsonValue = (value) => {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
  Object.values(value).forEach(deepFreezeJsonValue);
  return Object.freeze(value);
};

const signingResponseNeedsStaffRepair = (error) => {
  const response = error?.response;
  return response?.status === 409 &&
    response?.data?.retryable === false &&
    SIGNING_REPAIR_ERRORS.has(response?.data?.error);
};

const submitSigningRequest = async (id, values, {
  cloneEachAttempt = false,
  shouldContinue = () => true,
} = {}) => {
  for (let attempt = 0; ; attempt += 1) {
    if (!shouldContinue()) return null;
    try {
      const attemptValues = cloneEachAttempt ? cloneJsonValue(values) : values;
      return await api.post(`/api/signing-requests/${id}/sign`, attemptValues);
    } catch (err) {
      const response = err?.response;
      const retryable = (
        response?.status === 409 &&
        response?.data?.retryable === true &&
        RETRYABLE_SIGNING_ERRORS.has(response?.data?.error)
      );
      if (!retryable || attempt >= SIGNING_RETRY_DELAYS_MS.length) throw err;
      await wait(SIGNING_RETRY_DELAYS_MS[attempt]);
      if (!shouldContinue()) return null;
    }
  }
};

const normalizeValue = (comp, val) => {
  const n = (comp && typeof comp.normalize === 'string') ? comp.normalize.toLowerCase() : '';
  switch (n) {
    case 'number': {
      const num = Number(val);
      return Number.isFinite(num) ? num : val;
    }
    case 'boolean': {
      if (val === true || val === false) return val;
      const s = String(val).toLowerCase();
      return s === 'true' || s === '1' || s === 'yes' || s === 'y';
    }
    default:
      return val;
  }
};

const VALUE_COMPONENT_TYPES = new Set([
  'input',
  'text',
  'email',
  'phone',
  'password',
  'number',
  'textarea',
  'radio',
  'checkbox',
  'select',
  'date',
  'character-count',
  'file-upload',
  'checkboxes',
  'signature-ack'
]);

const cloneInitialValue = (value) => {
  if (Array.isArray(value)) return [...value];
  if (value && typeof value === 'object') return { ...value };
  return value;
};

const hasMeaningfulValue = (value) => {
  if (value === undefined || value === null || value === '') return false;
  if (Array.isArray(value)) return value.some(hasMeaningfulValue);
  if (typeof value === 'object') return Object.values(value).some(hasMeaningfulValue);
  return true;
};

const getRepeatableConfig = (comp) => {
  const raw = comp?.repeatable;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const group = String(raw.group || '').trim();
  const index = Number(raw.index);
  if (!group || !Number.isInteger(index) || index < 1) return null;
  const minItems = Math.max(1, Number.isInteger(Number(raw.minItems)) ? Number(raw.minItems) : 1);
  const maxItems = Math.max(minItems, Number.isInteger(Number(raw.maxItems)) ? Number(raw.maxItems) : index);
  return { ...raw, group, index, minItems, maxItems };
};

const resolveRepeatableCounts = (steps, initialValues) => {
  const groups = new Map();
  (steps || []).forEach(step => {
    (step?.components || []).forEach(comp => {
      const repeatable = getRepeatableConfig(comp);
      if (!repeatable) return;
      const existing = groups.get(repeatable.group) || {
        minItems: repeatable.minItems,
        maxItems: repeatable.maxItems,
        populatedItems: 0,
      };
      existing.minItems = Math.max(existing.minItems, repeatable.minItems);
      existing.maxItems = Math.max(existing.maxItems, repeatable.maxItems, repeatable.index);
      const key = comp.storageKey || comp.id;
      if (key && hasMeaningfulValue(initialValues?.[key])) {
        existing.populatedItems = Math.max(existing.populatedItems, repeatable.index);
      }
      groups.set(repeatable.group, existing);
    });
  });
  return Object.fromEntries(Array.from(groups.entries()).map(([group, config]) => [
    group,
    Math.min(config.maxItems, Math.max(config.minItems, config.populatedItems)),
  ]));
};

const resolveDynamicDateBounds = (comp, values) => {
  const monthField = String(comp?.dateBounds?.monthField || '').trim();
  if (!monthField || String(comp?.inputType || '').toLowerCase() !== 'date') return comp;
  const reportingMonth = String(values?.[monthField] || '').trim();
  const match = reportingMonth.match(/^(\d{4})-(0[1-9]|1[0-2])$/);
  if (!match) return comp;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const finalDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return {
    ...comp,
    min: `${reportingMonth}-01`,
    max: `${reportingMonth}-${String(finalDay).padStart(2, '0')}`,
  };
};

const collectComponentInitialValues = (steps = []) => {
  const initialValues = {};
  const visit = (comp) => {
    if (!comp || typeof comp !== 'object') return;
    const type = String(comp.type || '').toLowerCase();
    const key = comp.storageKey || comp.id;
    if (VALUE_COMPONENT_TYPES.has(type) && key && typeof comp.value !== 'undefined') {
      initialValues[key] = cloneInitialValue(comp.value);
    }
    if (Array.isArray(comp.options)) {
      comp.options.forEach(option => {
        if (Array.isArray(option?.children)) {
          option.children.forEach(visit);
        }
      });
    }
  };
  (steps || []).forEach(step => {
    (step?.components || []).forEach(visit);
  });
  return initialValues;
};

const resolveResumeValues = (data) => {
  if (
    Object.prototype.hasOwnProperty.call(data || {}, 'resume_values') &&
    data?.resume_values &&
    typeof data.resume_values === 'object' &&
    !Array.isArray(data.resume_values)
  ) {
    return data.resume_values;
  }
  return null;
};

const resolveInitialValues = (data) => {
  const resumeValues = resolveResumeValues(data);
  if (resumeValues) return cloneJsonValue(resumeValues);
  const steps = Array.isArray(data?.steps) ? data.steps : [];
  const defaults = collectComponentInitialValues(steps);
  const explicit =
    data?.initial_values ||
    data?.initialValues ||
    data?.meta?.initialValues ||
    data?.meta?.initial_values ||
    {};
  return {
    ...defaults,
    ...(explicit && typeof explicit === 'object' ? explicit : {})
  };
};

const FILE_UPLOAD_VALUE_KEYS = ['filePath', 'file_path', 'path', 'key', 'fileId', 'file_id', 'name', 'originalFilename', 'original_filename'];
const hasFileUploadValue = (val) => {
  if (val === undefined || val === null) return false;
  if (typeof val === 'string') return false;
  if (typeof val === 'number') return Number.isFinite(val);
  if (Array.isArray(val)) return val.some(hasFileUploadValue);
  if (typeof val === 'object') {
    return FILE_UPLOAD_VALUE_KEYS.some((k) => {
      const v = val[k];
      if (v === undefined || v === null) return false;
      if (typeof v === 'string') return v.trim() !== '';
      if (typeof v === 'number') return Number.isFinite(v);
      return false;
    });
  }
  return false;
};
const isFileUploadEmpty = (val) => !hasFileUploadValue(val);

const resolveLocalizedText = (value, language) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    if (typeof value[language] === 'string') return value[language];
    if (typeof value.en === 'string') return value.en;
    if (typeof value.fr === 'string') return value.fr;
    if (typeof value.text === 'string') return value.text;
  }
  return '';
};

const stripHtml = (value) =>
  String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const normalizeHeadingText = (value) =>
  stripHtml(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const isHeadingParagraph = (comp) => {
  if (String(comp?.type || '').toLowerCase() !== 'paragraph') return false;
  const classes = [comp?.class, comp?.classes, comp?.className]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return classes.includes('govuk-heading');
};

const getParagraphHeadingText = (comp, language) => {
  if (!isHeadingParagraph(comp)) return '';
  return (
    resolveLocalizedText(comp?.html, language) ||
    resolveLocalizedText(comp?.text, language) ||
    resolveLocalizedText(comp?.label, language) ||
    ''
  );
};

const DocumentDetail = () => {
  const { language } = useContext(LanguageContext);
  const t = useMemo(() => copy[language] || copy.en, [language]);
  const { id } = useParams();
  const navigate = useNavigate();
  const [reqData, setReqData] = useState(null);
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [financialRefreshRequired, setFinancialRefreshRequired] = useState(false);
  const [reloadVersion, setReloadVersion] = useState(0);
  const [currentStepId, setCurrentStepId] = useState(null);
  const [history, setHistory] = useState([]);
  const [repeatableCounts, setRepeatableCounts] = useState({});
  const errorSummaryRef = useRef(null);
  const frozenResumePayloadRef = useRef(null);
  const activeSigningRequestIdRef = useRef(String(id));
  activeSigningRequestIdRef.current = String(id);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      frozenResumePayloadRef.current = null;
      setLoading(true);
      setError('');
      setFinancialRefreshRequired(false);
      setSubmitted(false);
      setCurrentStepId(null);
      setHistory([]);
      try {
        const { data } = await api.get(`/api/signing-requests/${id}`);
        if (!cancelled) {
          const resumeValues = resolveResumeValues(data);
          const initialValues = resolveInitialValues(data);
          frozenResumePayloadRef.current = resumeValues
            ? deepFreezeJsonValue(cloneJsonValue(resumeValues))
            : null;
          setReqData(data);
          setValues(initialValues);
          setRepeatableCounts(resolveRepeatableCounts(data?.steps, initialValues));
          setErrors({});
          setSubmitted(String(data?.status || '').trim().toLowerCase() === 'signed');
        }
      } catch (err) {
        const needsStaffRepair = signingResponseNeedsStaffRepair(err);
        if (!needsStaffRepair) console.error('Failed to load signing request', err);
        if (!cancelled) setError(needsStaffRepair ? t.lineageRepair : t.loadError);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [id, reloadVersion, t.lineageRepair, t.loadError]);

  const hasFrozenResumePayload = resolveResumeValues(reqData) !== null;
  const steps = useMemo(() => Array.isArray(reqData?.steps) ? reqData.steps : [], [reqData]);
  const isDecisionLetter = useMemo(() => {
    const docType =
      reqData?.checklist_doc_type ||
      reqData?.document_type ||
      reqData?.documentType ||
      '';
    return ['assessment_approval_letter', 'assessment_denial_letter'].includes(String(docType || '').trim());
  }, [reqData]);
  useEffect(() => {
    if (!steps.length) {
      setCurrentStepId(null);
      setHistory([]);
      return;
    }
    setCurrentStepId(prev => (prev && steps.some(s => s.stepId === prev)) ? prev : steps[0]?.stepId || null);
  }, [steps]);

  const currentStep = useMemo(
    () => steps.find(s => s.stepId === currentStepId) || steps[0] || null,
    [steps, currentStepId]
  );
  const workflowValues = hasFrozenResumePayload && frozenResumePayloadRef.current
    ? frozenResumePayloadRef.current
    : values;
  const pageTitle = useMemo(
    () => (typeof reqData?.workflow_name === 'string' && reqData.workflow_name.trim()) || t.title,
    [reqData, t.title]
  );
  const currentStepTitle = useMemo(
    () => (currentStep ? resolveLocalizedText(currentStep.title, language) : ''),
    [currentStep, language]
  );
  const showStepTitle = useMemo(() => {
    if (!currentStepTitle) return false;
    if (steps.length <= 1) return false;
    return normalizeHeadingText(currentStepTitle) !== normalizeHeadingText(pageTitle);
  }, [currentStepTitle, pageTitle, steps.length]);
  const renderedComponents = useMemo(() => {
    if (!currentStep) return [];
    const components = Array.isArray(currentStep.components) ? [...currentStep.components] : [];
    if (!components.length) return components;
    const firstComponentHeading = getParagraphHeadingText(components[0], language);
    const firstHeadingNormalized = normalizeHeadingText(firstComponentHeading);
    if (
      firstHeadingNormalized &&
      (
        firstHeadingNormalized === normalizeHeadingText(pageTitle) ||
        (currentStepTitle && firstHeadingNormalized === normalizeHeadingText(currentStepTitle))
      )
    ) {
      components.shift();
    }
    return components;
  }, [currentStep, currentStepTitle, language, pageTitle]);

  const validateStep = (step, candidateValues = values) => {
    const newErrors = {};
    (step.components || []).forEach(comp => {
      const repeatable = getRepeatableConfig(comp);
      if (repeatable && repeatable.index > (repeatableCounts[repeatable.group] || repeatable.minItems)) return;
      const key = comp.storageKey || comp.id;
      const v = candidateValues[key];
      if (comp.required) {
        const type = String(comp.type || '').toLowerCase();
        const empty = type === 'file-upload'
          ? isFileUploadEmpty(v)
          : (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0));
        if (empty) newErrors[key] = (comp.validation?.requiredMessage?.[language] || comp.validation?.requiredMessage?.en || t.required);
      }
    });
    return newErrors;
  };

  const resolveNextStepId = (step, data) => {
    if (!step) return null;
    // Branching rules (json-logic)
    if (Array.isArray(step.branching) && step.branching.length) {
      for (const rule of step.branching) {
        try {
          if (rule && rule.condition && jsonLogic.apply(rule.condition, data)) {
            return rule.nextStepId || null;
          }
        } catch (_) {
          // ignore bad rules
        }
      }
      if (step.defaultNextStepId) return step.defaultNextStepId;
    }
    // Linear next
    if (step.nextStepId) return step.nextStepId;
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!steps.length || !currentStep) return;
    const newErrors = validateStep(currentStep, workflowValues);
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      if (errorSummaryRef.current) errorSummaryRef.current.focus();
      return;
    }
    const nextId = resolveNextStepId(currentStep, workflowValues);
    if (nextId) {
      setHistory(h => [...h, currentStep.stepId]);
      setCurrentStepId(nextId);
      return;
    }
    try {
      const submittedRequestId = String(id);
      const isCurrentRequest = () => activeSigningRequestIdRef.current === submittedRequestId;
      setLoading(true);
      await submitSigningRequest(id, workflowValues, {
        cloneEachAttempt: hasFrozenResumePayload,
        shouldContinue: isCurrentRequest,
      });
      if (!isCurrentRequest()) return;
      setSubmitted(true);
    } catch (err) {
      if (activeSigningRequestIdRef.current !== String(id)) return;
      const needsStaffRepair = signingResponseNeedsStaffRepair(err);
      if (!needsStaffRepair) console.error('Failed to submit signing request', err);
      const financialChanged = err?.response?.data?.error === 'financial_overview_changed';
      setFinancialRefreshRequired(financialChanged);
      setError(financialChanged ? t.financialChanged : (needsStaffRepair ? t.lineageRepair : t.loadError));
    } finally {
      if (activeSigningRequestIdRef.current === String(id)) setLoading(false);
    }
  };

  const handleBack = () => {
    setErrors({});
    setError('');
    setHistory(h => {
      if (!h.length) return h;
      const nextHist = [...h];
      const prev = nextHist.pop();
      setCurrentStepId(prev || steps[0]?.stepId || null);
      return nextHist;
    });
  };

  const renderComponent = (comp) => {
    const runtimeComp = resolveDynamicDateBounds(comp, values);
    const type = String(runtimeComp.type || '').toLowerCase();
    const Cmp = registry[type];
    const key = runtimeComp.storageKey || runtimeComp.id;
    if (!Cmp) {
      return <div key={runtimeComp.id} className="govuk-hint">Unsupported component: {type}</div>;
    }
    const props = { comp: runtimeComp, lang: language, values };
    const needsValue = ['input', 'text', 'email', 'phone', 'password', 'number', 'textarea', 'radio', 'checkbox', 'select', 'date', 'character-count', 'file-upload', 'checkboxes', 'signature-ack'].includes(type);
    if (needsValue) {
      props.value = values[key];
      props.onChange = (val) => setValues(s => ({ ...s, [key]: normalizeValue(runtimeComp, val) }));
      props.error = errors[key];
    }
    props.render = (child) => renderComponent(child);
    return <Cmp key={runtimeComp.id} {...props} />;
  };

  const renderStepComponents = () => {
    const output = [];
    const renderedGroups = new Set();
    renderedComponents.forEach(comp => {
      const repeatable = getRepeatableConfig(comp);
      if (!repeatable) {
        output.push(renderComponent(comp));
        return;
      }
      if (renderedGroups.has(repeatable.group)) return;
      renderedGroups.add(repeatable.group);

      const groupComponents = renderedComponents.filter(candidate => (
        getRepeatableConfig(candidate)?.group === repeatable.group
      ));
      const availableIndexes = Array.from(new Set(groupComponents.map(candidate => (
        getRepeatableConfig(candidate)?.index
      )).filter(Number.isInteger))).sort((a, b) => a - b);
      const availableMax = availableIndexes[availableIndexes.length - 1] || repeatable.minItems;
      const maxItems = Math.min(repeatable.maxItems, availableMax);
      const count = Math.min(maxItems, Math.max(
        repeatable.minItems,
        repeatableCounts[repeatable.group] || repeatable.minItems
      ));
      const addLabel = resolveLocalizedText(repeatable.addLabel, language) || 'Add another';
      const removeLabel = resolveLocalizedText(repeatable.removeLabel, language) || 'Remove';

      const addItem = () => {
        const nextIndex = Math.min(maxItems, count + 1);
        setRepeatableCounts(current => ({ ...current, [repeatable.group]: nextIndex }));
        const firstNewComponent = groupComponents.find(candidate => (
          getRepeatableConfig(candidate)?.index === nextIndex
        ));
        const focusId = firstNewComponent?.id || firstNewComponent?.storageKey;
        if (focusId && typeof window !== 'undefined') {
          window.setTimeout(() => document.getElementById(focusId)?.focus(), 0);
        }
      };

      const removeLastItem = () => {
        if (count <= repeatable.minItems) return;
        const removedKeys = groupComponents
          .filter(candidate => getRepeatableConfig(candidate)?.index === count)
          .map(candidate => candidate.storageKey || candidate.id)
          .filter(Boolean);
        setValues(current => {
          const next = { ...current };
          removedKeys.forEach(key => { delete next[key]; });
          return next;
        });
        setErrors(current => {
          const next = { ...current };
          removedKeys.forEach(key => { delete next[key]; });
          return next;
        });
        setRepeatableCounts(current => ({
          ...current,
          [repeatable.group]: Math.max(repeatable.minItems, count - 1),
        }));
      };

      output.push(
        <div key={`repeatable-${repeatable.group}`} className="repeatable-group">
          {availableIndexes.filter(index => index <= count).map(index => (
            <div
              key={`${repeatable.group}-${index}`}
              id={`${repeatable.group}-item-${index}`}
              className={`repeatable-group__item${index > repeatable.minItems ? ' repeatable-group__item--additional' : ''}`}
            >
              {groupComponents
                .filter(candidate => getRepeatableConfig(candidate)?.index === index)
                .map(renderComponent)}
              {index === count && count > repeatable.minItems && (
                <button
                  type="button"
                  className="govuk-button govuk-button--secondary repeatable-group__remove"
                  onClick={removeLastItem}
                >
                  {removeLabel}
                </button>
              )}
            </div>
          ))}
          {count < maxItems && (
            <button
              type="button"
              className="govuk-button govuk-button--secondary repeatable-group__add"
              aria-controls={`${repeatable.group}-item-${count + 1}`}
              onClick={addItem}
            >
              {addLabel}
            </button>
          )}
        </div>
      );
    });
    return output;
  };

  if (loading) {
    return (
      <div className="govuk-width-container govuk-!-margin-top-3">
        <main className="govuk-main-wrapper" role="main">
          <p className="govuk-body">{t.loading}</p>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="govuk-width-container govuk-!-margin-top-3">
        <main className="govuk-main-wrapper" role="main">
          <div className="govuk-error-summary" role="alert" aria-live="assertive">
            <h2 className="govuk-error-summary__title" ref={errorSummaryRef}>{error}</h2>
          </div>
          {financialRefreshRequired && (
            <button type="button" className="govuk-button" onClick={() => setReloadVersion(v => v + 1)}>{t.reloadFinancial}</button>
          )}
          <button type="button" className="govuk-button govuk-button--secondary" onClick={() => navigate('/documents')}>{t.back}</button>
        </main>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="govuk-width-container govuk-!-margin-top-3">
        <main className="govuk-main-wrapper" role="main">
          <h1 className="govuk-heading-l">{t.submitted}</h1>
          <p className="govuk-body">{t.submittedMsg}</p>
          <button type="button" className="govuk-button" onClick={() => navigate('/documents')}>{t.back}</button>
        </main>
      </div>
    );
  }

  if (!steps.length) {
    return (
      <div className="govuk-width-container govuk-!-margin-top-3">
        <main className="govuk-main-wrapper" role="main">
          <p className="govuk-body">{t.schemaMissing}</p>
          <button type="button" className="govuk-button govuk-button--secondary" onClick={() => navigate('/documents')}>{t.back}</button>
        </main>
      </div>
    );
  }

  return (
    <div className="govuk-width-container govuk-!-margin-top-3">
      <main className="govuk-main-wrapper" role="main">
        <h1 className="govuk-heading-l">{pageTitle}</h1>
        {Object.keys(errors).length > 0 && (
          <div className="govuk-error-summary" role="alert" aria-live="assertive" tabIndex="-1" ref={errorSummaryRef}>
            <h2 className="govuk-error-summary__title">{t.required}</h2>
            <div className="govuk-error-summary__body">
              <ul className="govuk-list govuk-error-summary__list">
                {Object.entries(errors).map(([k, v]) => (
                  <li key={k}>{v}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
        {hasFrozenResumePayload && (
          <div id="signing-resume-read-only" className="govuk-inset-text" role="status">
            {t.resumedLocked}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="govuk-form-group">
        {currentStep ? (
              <div key={currentStep.stepId || currentStep.id} className="govuk-!-margin-bottom-6">
                {showStepTitle && (
                  <h2 className="govuk-heading-m">
                    {currentStepTitle}
                  </h2>
                )}
                {currentStep.description && (
                  <p className="govuk-body">
                    {resolveLocalizedText(currentStep.description, language)}
                  </p>
                )}
                {hasFrozenResumePayload ? (
                  <fieldset
                    className="govuk-fieldset"
                    disabled
                    aria-describedby="signing-resume-read-only"
                  >
                    <legend className="govuk-visually-hidden">{t.resumedLockedLegend}</legend>
                    {renderStepComponents()}
                  </fieldset>
                ) : renderStepComponents()}
              </div>
            ) : (
              <p className="govuk-body">{t.schemaMissing}</p>
            )}
          </div>
          <div className="govuk-button-group">
            <button
              type="button"
              className="govuk-button govuk-button--secondary"
              onClick={handleBack}
              disabled={history.length === 0 || loading}
            >
              Back
            </button>
            <button
              type="submit"
              className="govuk-button"
              disabled={loading}
              aria-busy={loading ? 'true' : undefined}
            >
              {resolveNextStepId(currentStep, workflowValues) ? 'Next' : (isDecisionLetter ? t.savePdf : t.submit)}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default DocumentDetail;
